import { CheckCircle2 } from "lucide-react";
import {
  Body,
  Card,
  CardTitle,
  Caption,
  Subtitle,
  VCheckbox,
  VField,
  VInput,
} from "@/components/ui";
import { useAssetTypeLabel } from "@/domains/assets";
import type { CreateRequestInput, RequestedAssetLine } from "@/domains/requests/api/types";

type StepSubmitProps = {
  title: string;
  qualityConfirmed: boolean;
  draft: CreateRequestInput;
  onChangeTitle: (next: string) => void;
  onChangeQualityConfirmed: (next: boolean) => void;
};

export function StepSubmit({
  title,
  qualityConfirmed,
  draft,
  onChangeTitle,
  onChangeQualityConfirmed,
}: StepSubmitProps): React.JSX.Element {
  return (
    <div className="space-y-4">
      <Card padding="lg" className="space-y-4">
        <VField
          label="25. Titel aanvraag"
          htmlFor="title"
          required
          hint="Geef je aanvraag een titel. Deze titel wordt later gebruikt voor alle communicatie i.v.m. je aanvraag."
        >
          <VInput
            id="title"
            value={title}
            onChange={(e) => onChangeTitle(e.target.value)}
          />
        </VField>

        <VField label="26. Kwaliteit en inhoud" required>
          <VCheckbox
            id="quality"
            checked={qualityConfirmed}
            onCheckedChange={(checked) => onChangeQualityConfirmed(checked === true)}
            label="Ik bevestig de kwaliteit en de inhoud van de beelden te zullen nakijken conform het concept mobiele camera's en hun relevantie."
          />
        </VField>
      </Card>

      <Card padding="lg" className="space-y-3 border-success-200 bg-success-50">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="size-5 text-success-700" aria-hidden />
          <CardTitle className="text-success-800">Overzicht</CardTitle>
        </div>
        <Subtitle className="text-success-700">
          Controleer de gegevens. Na indienen krijg je een aanvraagnummer en wordt het dossier aangemaakt.
        </Subtitle>
        <dl className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
          <Row label="Aanvrager">
            {draft.requester.firstName} {draft.requester.lastName} — {draft.requester.service}
          </Row>
          <Row label="Materiaal" full>
            <AssetsSummary lines={draft.assets} />
          </Row>
          <Row label="Periode">
            {draft.period.startDate} → {draft.period.endDate}
            {draft.period.extensionPossible ? " (verlenging mogelijk)" : ""}
          </Row>
          <Row label="Locatie" full>
            {draft.location.address}
          </Row>
          <Row label="Kijkrichting" full>
            {draft.location.locationAndCameraDirection || "—"}
          </Row>
          <Row label="Reden" full>
            {draft.motivation.reason || "—"}
          </Row>
          <Row label="Foto's">{draft.location.photos.length} bestand(en)</Row>
          <Row label="Evenement">{draft.extraInfo.event || "Geen"}</Row>
        </dl>
      </Card>
    </div>
  );
}

function Row({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}): React.JSX.Element {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Caption tone="muted">{label}</Caption>
      <Body weight="medium" tone="success">
        {children}
      </Body>
    </div>
  );
}

function AssetsSummary({
  lines,
}: {
  lines: ReadonlyArray<RequestedAssetLine>;
}): React.JSX.Element {
  if (lines.length === 0) return <>Geen materiaal toegevoegd.</>;
  return (
    <ul className="space-y-0.5">
      {lines.map((line) => (
        <AssetsSummaryItem key={line.id} line={line} />
      ))}
    </ul>
  );
}

function AssetsSummaryItem({ line }: { line: RequestedAssetLine }): React.JSX.Element {
  const label = useAssetTypeLabel(line.assetType);
  return (
    <li>
      {line.quantity}× {label || "—"}
    </li>
  );
}
